import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalyticsConsent } from '@/context/AnalyticsConsentContext';
import {
  isFirebaseAnalyticsEnabled,
  setFirebaseAnalyticsConsent,
  setFirebaseDefaultPageContext,
  trackPageView,
} from '@/lib/firebase';
import { getAnalyticsPageContext } from '@/lib/firebase/page-context';
import { resolveAnalyticsPagePath } from '@/lib/firebase/routes';

export const FirebaseAnalytics = () => {
  const { pathname } = useLocation();
  const { status } = useAnalyticsConsent();
  const lastTrackedPathname = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'granted') {
      lastTrackedPathname.current = null;
      void setFirebaseAnalyticsConsent('denied');
      return;
    }

    if (!isFirebaseAnalyticsEnabled() || lastTrackedPathname.current === pathname) {
      return;
    }

    // Let redirect-only routes settle before recording the rendered destination.
    const trackingTimer = window.setTimeout(() => {
      lastTrackedPathname.current = pathname;
      const pagePath = resolveAnalyticsPagePath(pathname);
      if (!pagePath) {
        void setFirebaseAnalyticsConsent('granted');
        return;
      }

      const pageContext = getAnalyticsPageContext({
        pathname,
        origin: window.location.origin,
        referrer: document.referrer,
      });

      void setFirebaseAnalyticsConsent('granted')
        .then(() => setFirebaseDefaultPageContext(pageContext))
        .then(() => trackPageView(pageContext))
        .catch(() => {
          if (lastTrackedPathname.current === pathname) {
            lastTrackedPathname.current = null;
          }
        });
    }, 0);

    return () => {
      window.clearTimeout(trackingTimer);
    };
  }, [pathname, status]);

  return null;
};
